/**
 * File Upload Utilities
 *
 * Centralized utilities for file upload operations to eliminate code duplication.
 * Provides reusable functions for:
 * - Obtaining signed URLs for upload/download
 * - Uploading files with optional progress tracking
 *
 * @example
 * // Get signed URL and upload a file
 * const signedUrl = await getSignedUrl(file.name, 'application/pdf', 'my-bucket');
 * await uploadFile(signedUrl, file, 'application/pdf');
 *
 * @example
 * // Upload with progress tracking
 * await uploadFile(signedUrl, file, 'application/pdf', (progress) => {
 *   console.log(`${progress.percentage}% uploaded`);
 * });
 */

import { config } from '../config/env.config';
import type { AxiosProgressEvent } from 'axios';
import { sanitizeUrl } from './url';
import { UploadProgress } from '../types/api';
import { apiClient } from '../services/apiClient';
import { logError, logInfo } from './logging';

const DEFAULT_UPLOAD_CHUNK_SIZE_BYTES = 5 * 1024 * 1024;
const DEFAULT_DOWNLOAD_CHUNK_SIZE_BYTES = 5 * 1024 * 1024;
const uploadChunkSizeByUrl = new Map<string, number>();

function usesPostDownloadProxy(url: string): boolean {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.pathname.endsWith('/download-signed-url') || parsed.searchParams.has('artifact_id');
  } catch {
    return url.includes('/download-signed-url') || url.includes('artifact_id=');
  }
}

function usesPostUploadProxy(url: string): boolean {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.pathname.endsWith('/upload-signed-url') || parsed.searchParams.has('upload_id');
  } catch {
    return url.includes('/upload-signed-url') || url.includes('upload_id=');
  }
}

function buildUrlWithQueryParams(url: string, params: Record<string, string>): string {
  const parsed = new URL(url, window.location.origin);
  Object.entries(params).forEach(([key, value]) => parsed.searchParams.set(key, value));
  return parsed.toString();
}

function emitUploadProgress(
  onProgress: ((progress: UploadProgress) => void) | undefined,
  loaded: number,
  total: number
): void {
  if (!onProgress || total <= 0) {
    return;
  }

  const percentage = Math.min(100, Math.round((loaded * 100) / total));
  onProgress({ loaded, total, percentage });
}

async function uploadFileInChunks(
  safeUrl: string,
  file: File,
  contentType: string,
  onProgress?: (progress: UploadProgress) => void
): Promise<void> {
  const axiosInstance = apiClient.getAxiosInstance();
  const totalSize = file.size;
  const chunkSize = uploadChunkSizeByUrl.get(safeUrl) ?? DEFAULT_UPLOAD_CHUNK_SIZE_BYTES;
  const totalParts = Math.max(1, Math.ceil(totalSize / chunkSize));
  let uploadedBytes = 0;

  logInfo(`Upload proxy: avviato upload chunked in ${totalParts} parti`);

  for (let partNumber = 1; partNumber <= totalParts; partNumber += 1) {
    const start = (partNumber - 1) * chunkSize;
    const end = Math.min(start + chunkSize, totalSize);
    const chunk = file.slice(start, end);
    const chunkUrl = buildUrlWithQueryParams(safeUrl, { part_number: String(partNumber) });

    logInfo(`Upload proxy: invio chunk ${partNumber} di ${totalParts}`);

    await axiosInstance.post(chunkUrl, chunk, {
      timeout: 0,
      headers: {
        'Content-Type': contentType
      },
      onUploadProgress: (progressEvent: AxiosProgressEvent) => {
        const currentChunkLoaded = Math.min(progressEvent.loaded, chunk.size);
        emitUploadProgress(onProgress, uploadedBytes + currentChunkLoaded, totalSize);
      }
    });

    uploadedBytes += chunk.size;
    emitUploadProgress(onProgress, uploadedBytes, totalSize);
  }

  const completeUrl = buildUrlWithQueryParams(safeUrl, { complete: 'true' });
  logInfo('Upload proxy: completamento upload chunked');

  await axiosInstance.post(completeUrl, {
    total_parts: totalParts,
    total_size_bytes: totalSize,
  }, {
    timeout: 0,
  });

  emitUploadProgress(onProgress, totalSize, totalSize);
  uploadChunkSizeByUrl.delete(safeUrl);
  logInfo('Upload proxy: upload chunked completato');
}

async function fetchBlobFromUrl(
  url: string,
  onDownloadProgress?: (loaded: number, total: number) => void
): Promise<Blob> {
  const safeUrl = sanitizeUrl(url);
  if (!safeUrl) {
    throw new Error('Invalid download URL');
  }

  if (usesPostDownloadProxy(safeUrl)) {
    return await fetchBlobFromProxyInChunks(safeUrl, onDownloadProgress);
  }

  const axiosInstance = apiClient.getAxiosInstance();
  const requestConfig = {
    responseType: 'blob' as const,
    onDownloadProgress: onDownloadProgress
      ? (progressEvent: AxiosProgressEvent) => {
          onDownloadProgress(progressEvent.loaded, progressEvent.total ?? 0);
        }
      : undefined,
  };

  const response = await axiosInstance.get<Blob>(safeUrl, requestConfig);
  return response.data;
}

function parseTotalSizeFromContentRange(contentRange?: string): number | undefined {
  if (!contentRange) {
    return undefined;
  }

  const match = /^bytes\s+\d+-\d+\/(\d+)$/.exec(contentRange);
  if (!match) {
    return undefined;
  }

  return Number(match[1]);
}

async function requestDownloadChunk(
  safeUrl: string,
  start: number,
  end: number,
  onDownloadProgress?: (loaded: number, total: number) => void
) {
  return await apiClient.getAxiosInstance().post<Blob>(safeUrl, undefined, {
    timeout: 0,
    responseType: 'blob',
    headers: {
      Range: `bytes=${start}-${end}`,
    },
    onDownloadProgress: onDownloadProgress
      ? (progressEvent: AxiosProgressEvent) => {
          onDownloadProgress(progressEvent.loaded, progressEvent.total ?? 0);
        }
      : undefined,
  });
}

async function fetchBlobFromProxyInChunks(
  safeUrl: string,
  onDownloadProgress?: (loaded: number, total: number) => void
): Promise<Blob> {
  const chunks: Blob[] = [];
  let start = 0;
  let totalSize: number | undefined;
  let downloadedBytes = 0;

  while (true) {
    const end = start + DEFAULT_DOWNLOAD_CHUNK_SIZE_BYTES - 1;
    const response = await requestDownloadChunk(safeUrl, start, end, (loaded, total) => {
      const effectiveTotal = totalSize ?? total;
      if (onDownloadProgress && effectiveTotal > 0) {
        onDownloadProgress(downloadedBytes + loaded, effectiveTotal);
      }
    });

    const responseBlob = response.data;
    chunks.push(responseBlob);
    downloadedBytes += responseBlob.size;

    const totalFromRange = parseTotalSizeFromContentRange(response.headers['content-range']);
    const totalFromLength = Number(response.headers['content-length']);
    totalSize = totalFromRange ?? (Number.isFinite(totalFromLength) && totalFromLength > 0 ? totalFromLength : totalSize);

    if (!totalSize || downloadedBytes >= totalSize || responseBlob.size < DEFAULT_DOWNLOAD_CHUNK_SIZE_BYTES) {
      if (onDownloadProgress && totalSize) {
        onDownloadProgress(downloadedBytes, totalSize);
      }
      return new Blob(chunks, {
        type: response.headers['content-type'] || 'application/octet-stream'
      });
    }

    start = downloadedBytes;
  }
}

async function fetchTextFromUrl(url: string, maxBytes?: number): Promise<string> {
  const safeUrl = sanitizeUrl(url);
  if (!safeUrl) {
    throw new Error('Invalid download URL');
  }

  if (usesPostDownloadProxy(safeUrl)) {
    const end = Math.max(0, (maxBytes ?? DEFAULT_DOWNLOAD_CHUNK_SIZE_BYTES) - 1);
    const response = await requestDownloadChunk(safeUrl, 0, end);
    return await response.data.text();
  }

  const axiosInstance = apiClient.getAxiosInstance();
  const response = await axiosInstance.get<string>(safeUrl, {
    responseType: 'text',
  });
  return maxBytes ? response.data.slice(0, maxBytes) : response.data;
}

/**
 * Get a signed URL for uploading a file to cloud storage
 *
 * @param filename - Name of the file to upload
 * @param contentType - MIME type of the file (e.g., 'application/pdf', 'application/zip')
 * @param bucketName - Name of the storage bucket (defaults to compare bucket)
 * @param pathPrefix - Optional path prefix within the bucket (e.g., 'vanilla/')
 * @returns The signed URL as a string
 *
 * @throws Error if the response is invalid or the request fails
 */
export async function getSignedUrl(
  filename: string,
  contentType: string = 'application/pdf',
  bucketName: string = config.storage.compareBucketName,
  pathPrefix: string = 'vanilla/'
): Promise<string> {
  const blobName = `${pathPrefix}${filename}`;

  const data = await apiClient.post(config.api.endpoints.uploadSignedUrl, {
    bucket_name: bucketName,
    blob_name: blobName,
    content_type: contentType
  });

  if (typeof data === 'object' && data.url) {
    const safeUrl = sanitizeUrl(data.url);
    const chunkSize = Number(data.chunk_size_bytes);

    if (safeUrl && Number.isFinite(chunkSize) && chunkSize > 0) {
      uploadChunkSizeByUrl.set(safeUrl, chunkSize);
      logInfo('Upload proxy: sessione di upload inizializzata');
    }

    return data.url;
  }

  return data;
}

/**
 * Get a signed URL for downloading a file from cloud storage
 *
 * @param blobName - Full path of the blob in storage (e.g., 'vanilla/report.xlsx')
 * @param bucketName - Name of the storage bucket (defaults to compare bucket)
 * @returns The signed URL as a string
 *
 * @throws Error if the response is invalid or the request fails
 */
export async function getDownloadSignedUrl(
  blobName: string,
  bucketName: string = config.storage.compareBucketName
): Promise<string> {
  const data = await apiClient.post(config.api.endpoints.downloadSignedUrl, {
    bucket_name: bucketName,
    blob_name: blobName
  });

  // Handle both string and object responses
  if (typeof data === 'object' && data.url) {
    return data.url;
  }

  return data;
}

/**
 * Upload a file to a signed URL
 *
 * @param signedUrl - The pre-signed URL obtained from getSignedUrl
 * @param file - The File object to upload
 * @param contentType - MIME type of the file (e.g., 'application/pdf', 'application/zip')
 * @param onProgress - Optional callback to track upload progress
 *
 * @throws Error if the upload fails
 *
 * @example
 * // Simple upload
 * await uploadFile(signedUrl, file, 'application/pdf');
 *
 * @example
 * // Upload with progress tracking
 * await uploadFile(signedUrl, file, 'application/pdf', (progress) => {
 *   setUploadProgress(progress);
 * });
 */
export async function uploadFile(
  signedUrl: string,
  file: File,
  contentType: string = 'application/pdf',
  onProgress?: (progress: UploadProgress) => void
): Promise<void> {
  const safeUrl = sanitizeUrl(signedUrl);
  if (!safeUrl) {
    throw new Error('Invalid upload URL');
  }

  if (usesPostUploadProxy(safeUrl)) {
    try {
      await uploadFileInChunks(safeUrl, file, contentType, onProgress);
      return;
    } catch (error) {
      uploadChunkSizeByUrl.delete(safeUrl);
      logError('Upload proxy: errore durante upload chunked');
      throw error;
    }
  }

  await apiClient.getAxiosInstance().post(safeUrl, file, {
    timeout: 0,
    headers: {
      'Content-Type': contentType
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress({
          loaded: progressEvent.loaded,
          total: progressEvent.total,
          percentage
        });
      }
    }
  });

}

/**
 * Download a file from a signed URL and trigger browser download
 *
 * @param signedUrl - The pre-signed download URL
 * @param filename - Desired filename for the downloaded file
 *
 * @throws Error if the download fails
 *
 * @example
 * const downloadUrl = await getDownloadSignedUrl('vanilla/report.xlsx');
 * await downloadFile(downloadUrl, 'similarity_report.xlsx');
 */
export async function downloadFile(
  signedUrl: string,
  filename: string
): Promise<void> {
  const blob = await fetchBlobFromUrl(signedUrl);
  const blobUrl = window.URL.createObjectURL(blob);
  const safeBlobUrl = sanitizeUrl(blobUrl, ['blob:']);
  if (!safeBlobUrl) {
    throw new Error('Invalid download URL');
  }

  // Explicit guard: ensures only blob: URLs are assigned to href, preventing XSS (CWE-80)
  if (!safeBlobUrl.startsWith('blob:')) {
    throw new Error('Unexpected URL protocol in download URL');
  }

  // Sanitize filename to prevent attribute injection via special characters
  const safeFilename = filename.replace(/[<>"'`]/g, '');

  const a = document.createElement('a');
  a.href = encodeURI(safeBlobUrl);
  a.download = safeFilename;
  a.click();
  window.URL.revokeObjectURL(blobUrl);
}

/**
 * Fetch text content from a signed URL
 *
 * @param signedUrl - The pre-signed URL
 * @returns The response body as text
 */
export async function fetchTextFromSignedUrl(signedUrl: string, maxBytes?: number): Promise<string> {
  return await fetchTextFromUrl(signedUrl, maxBytes);
}

export async function fetchBlobFromSignedUrl(
  signedUrl: string,
  onDownloadProgress?: (loaded: number, total: number) => void
): Promise<Blob> {
  return await fetchBlobFromUrl(signedUrl, onDownloadProgress);
}
